import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { ASSET } from "../../lib/api";
import KPICard from "../../components/KPICard";
import { ShoppingBag, Users, Wine, Package, FileText, AlertCircle, TrendingUp, Euro, Flame, MapPin, FileDown } from "lucide-react";

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [trending, setTrending] = useState({ wines: [], boxes: [], experiences: [] });
  useEffect(() => {
    api.get("/admin/dashboard").then(r => setData(r.data));
    api.get("/admin/alerts").then(r => setAlerts(r.data.alerts || [])).catch(() => {});
    api.get("/admin/trending").then(r => setTrending(r.data)).catch(() => {});
  }, []);
  if (!data) return <div>Caricamento…</div>;
  const k = data.kpi;
  return (
    <div data-testid="admin-dashboard">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--primary))] font-semibold">Panoramica</div>
          <h1 className="editorial-heading text-4xl md:text-5xl mt-1">Dashboard</h1>
        </div>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/pitch/download`}
          data-testid="admin-pitch-download"
          className="inline-flex items-center gap-2 px-5 py-3 bg-[hsl(var(--accent))] text-black hover:bg-[hsl(var(--accent))]/90 text-xs uppercase tracking-[0.25em] font-semibold transition-colors"
        >
          <FileDown className="h-4 w-4" /> Scarica Pitch Deck
        </a>
      </div>
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard label="Fatturato" value={`€${k.revenue.toFixed(0)}`} icon={Euro} accent />
        <KPICard label="Ordini" value={k.total_orders} suffix={`${k.confirmed_orders} pagati`} icon={ShoppingBag} />
        <KPICard label="Utenti" value={k.total_users} icon={Users} />
        <KPICard label="Vini attivi" value={k.total_wines} icon={Wine} />
        <KPICard label="Box attive" value={k.total_boxes} icon={Package} />
        <KPICard label="Preventivi" value={k.total_quotes} icon={FileText} />
        <KPICard label="Leads" value={k.total_leads} icon={TrendingUp} />
        <KPICard label="Margine medio" value={`${k.avg_margin_pct}%`} accent />
      </div>

      <div className="mt-10 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-card border border-border rounded-sm p-6">
          <div className="text-xs uppercase tracking-[0.2em] font-bold mb-4">Stock totale bottiglie</div>
          <div className="grid grid-cols-2 gap-4">
            <div><div className="text-xs uppercase text-muted-foreground">Disponibile</div><div className="text-3xl editorial-heading text-[hsl(var(--primary))]">{k.total_stock_bottles.toLocaleString()}</div></div>
            <div><div className="text-xs uppercase text-muted-foreground">Riservato</div><div className="text-3xl editorial-heading text-[hsl(var(--accent))]">{k.reserved_stock.toLocaleString()}</div></div>
          </div>
          <div className="mt-6 pt-4 border-t border-border">
            <div className="text-xs uppercase tracking-[0.2em] font-bold mb-3">Top box</div>
            <div className="space-y-2">
              {data.top_boxes.map(b => (
                <div key={b.id} className="flex items-center gap-3 text-sm">
                  <img src={b.image} alt="" className="w-10 h-10 object-cover rounded-sm" />
                  <div className="flex-1 truncate">{b.name}</div>
                  <div className="font-bold text-[hsl(var(--primary))]">€{b.price.toFixed(2)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-sm p-6">
          <div className="text-xs uppercase tracking-[0.2em] font-bold mb-4 flex items-center gap-2"><AlertCircle className="h-4 w-4 text-destructive"/>Alerts AI ({alerts.length})</div>
          <div className="space-y-2 max-h-[380px] overflow-y-auto">
            {alerts.length === 0 && <div className="text-sm text-muted-foreground">Nessun alert</div>}
            {alerts.map((a, i) => (
              <div key={i} className={`text-xs border-l-2 pl-3 py-1.5 ${a.severity === "high" ? "border-destructive" : a.severity === "medium" ? "border-[hsl(var(--accent))]" : "border-[hsl(var(--secondary))]"}`}>
                <div className="uppercase tracking-wider font-bold text-[10px] opacity-60">{a.type}</div>
                <div>{a.message}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* TRENDING WIDGET */}
      <div data-testid="trending-widget" className="mt-10 bg-card border border-border rounded-sm p-6">
        <div className="flex items-center gap-2 mb-1">
          <Flame className="h-5 w-5 text-[hsl(var(--accent))]" />
          <div className="text-xs uppercase tracking-[0.2em] font-bold">Trending Now · AI Featured</div>
        </div>
        <p className="text-sm text-muted-foreground mb-5">Top 5 prodotti per categoria, ordinati per viste + add-to-cart (peso ×5). I 4 con score più alto vengono promossi automaticamente in Home e nelle pagine prodotto.</p>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { key: "wines", label: "Vini", icon: Wine, link: "/vini" },
            { key: "boxes", label: "Box", icon: Package, link: "/box" },
            { key: "experiences", label: "Esperienze", icon: MapPin, link: "/esperienze" },
          ].map(({ key, label, icon: Icon, link }) => (
            <div key={key} data-testid={`trending-${key}`} className="border border-border rounded-sm overflow-hidden">
              <div className="px-4 py-2.5 bg-muted/50 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold">
                  <Icon className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />{label}
                </div>
                <Link to={link} className="text-[10px] uppercase tracking-wider text-[hsl(var(--accent))] hover:underline">Vedi →</Link>
              </div>
              <div className="divide-y divide-border">
                {(trending[key] || []).length === 0 && (
                  <div className="p-4 text-xs text-muted-foreground italic">Nessun dato — i prodotti verranno tracciati alle prossime visite.</div>
                )}
                {(trending[key] || []).map((p, i) => (
                  <div key={p.id} className="p-3 flex items-center gap-3 text-sm">
                    <div className="text-[10px] font-bold text-[hsl(var(--accent))] w-5">#{i + 1}</div>
                    {p.image && <img src={ASSET(p.image)} alt="" className="w-10 h-10 object-cover rounded-sm bg-white" />}
                    <div className="flex-1 min-w-0">
                      <div className="truncate font-medium">{p.name}</div>
                      <div className="text-[10px] text-muted-foreground flex gap-3">
                        <span>{p.views || 0} viste</span>
                        <span>{p.cart_adds || 0} cart</span>
                        <span className="text-[hsl(var(--primary))] font-bold">score {p.trend_score?.toFixed(0) || 0}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
